import React from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rneui/themed';

import Button from '../../../components/themed/Button';
import Text from '../../../components/ui-controls/Text';
import {lineHeight, space, type} from '../../../design-system/tokens';
import theme from '../../../style/theme';
import rewardsTheme from '../../../theme/rewardsTheme';

type OfferPanelLayoutProps = {
  testID: string;
  heading: string;
  body?: string;
  children: React.ReactNode;
};

// The root provider has no `LMGButton`, so the panel buttons need the brand theme here.
const OfferPanelLayout = ({testID, heading, body, children}: OfferPanelLayoutProps) => (
  <ThemeProvider theme={rewardsTheme}>
    <ScrollView contentContainerStyle={styles.scrollContainer} showsVerticalScrollIndicator={false}>
      <View style={styles.container} testID={testID}>
        <Text style={styles.headline} accessibilityRole="header">
          {heading}
        </Text>
        {body ? <Text style={styles.description}>{body}</Text> : null}
        <View style={styles.buttonContainer}>{children}</View>
      </View>
    </ScrollView>
  </ThemeProvider>
);

export default OfferPanelLayout;

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: space.s24,
    paddingVertical: space.s32,
    minHeight: 400,
  },
  headline: {
    ...type.heading.h1,
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
    marginBottom: space.s16,
  },
  description: {
    ...type.paragraph.p2,
    ...lineHeight.paragraph.p2,
    color: theme.colour.text.content.primary,
    textAlign: 'center',
    marginBottom: space.s32,
    paddingHorizontal: space.s8,
  },
  buttonContainer: {
    width: '100%',
    maxWidth: 300,
    gap: space.s12,
  },
});

/**
 * The themed Button, typed with `LMGButton`'s `variant`. `withTheme<ButtonProps>` in
 * `components/themed/Button.tsx` does not type it, so a bare `variant` fails type-check.
 */
export const PanelButton = Button as React.ComponentType<
  React.ComponentProps<typeof Button> & {variant?: 'outlined' | 'text'}
>;
